import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import { FaCheck, FaExclamation, FaInstagram, FaRobot } from "react-icons/fa";
import AgentsService from "services/admin/AgentsService";
import {
  INSTAGRAM_GRADIENT,
  InstagramConnectLayout,
  InstagramIllustration,
} from "pages/Admin/Channels/components/InstagramConnectLayout";

type Status = "loading" | "success" | "error";

interface ConnectedAccount {
  channelConnectionID: number;
  username: string | null;
  photo: string | null;
}

/** Понятный текст для частых ошибок Instagram */
const humanizeError = (message: string) => {
  if (/redirect_uri/i.test(message))
    return "Адрес возврата не совпадает с настройками приложения в Meta.";
  if (/has been used/i.test(message))
    return "Ссылка для входа уже использована. Войдите ещё раз.";
  if (/expired/i.test(message))
    return "Время на вход истекло. Войдите ещё раз.";
  return "Instagram не подтвердил вход. Попробуйте ещё раз.";
};

/** Аватар подключённого аккаунта с зелёной галочкой */
const AccountIllustration = ({ photo }: { photo: string | null }) => (
  <div className="relative w-[150px] h-[150px] sm:w-[180px] sm:h-[180px]">
    <div
      className="w-full h-full rounded-full p-[5px]"
      style={{
        background: INSTAGRAM_GRADIENT,
        boxShadow: "0 24px 60px rgba(214,41,118,0.45)",
      }}
    >
      <div className="w-full h-full rounded-full bg-[#351a4b] p-[4px]">
        {photo ? (
          <img
            src={photo}
            alt=""
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <div className="w-full h-full rounded-full bg-white/10 flex items-center justify-center">
            <FaInstagram className="w-1/2 h-1/2 text-white" />
          </div>
        )}
      </div>
    </div>
    <div className="absolute right-1 bottom-1 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-500 border-4 border-[#351a4b] flex items-center justify-center text-white">
      <FaCheck className="w-5 h-5 sm:w-6 sm:h-6" />
    </div>
  </div>
);

/**
 * Возврат после входа через Instagram (адрес возврата в Meta — этот маршрут, менять нельзя).
 * Обмениваем code на токен и показываем «Instagram подключён» с кнопкой «Создать чат-бота».
 */
const InstagramOAuth = () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<{ text: string; details?: string } | null>(
    null,
  );
  const [account, setAccount] = useState<ConnectedAccount | null>(null);
  // code одноразовый — не отправляем его повторно (StrictMode, повторный рендер)
  const isExchangeStarted = useRef(false);

  const stateData = (() => {
    try {
      return JSON.parse(state || "{}");
    } catch {
      return {};
    }
  })();
  const isOnboarding = stateData.returnTo === "onboarding";

  useEffect(() => {
    if (isExchangeStarted.current) return;
    isExchangeStarted.current = true;

    const exchange = async () => {
      // Пользователь нажал «Отмена» на странице Instagram
      const denied = params.get("error_reason") || params.get("error");
      if (denied) {
        setError({
          text: "Вы отменили вход в Instagram.",
          details: params.get("error_description") || undefined,
        });
        setStatus("error");
        return;
      }
      if (!code || !stateData.channelConnectionID || !stateData.orgID) {
        setError({
          text: "Instagram вернул неполные данные. Попробуйте войти ещё раз.",
        });
        setStatus("error");
        return;
      }

      try {
        const channelConnectionID = Number(stateData.channelConnectionID);
        const { data } = await AgentsService.getInstagramToken(
          channelConnectionID,
          code,
          state,
        );
        const channel = data?.channelConnection;
        setAccount({
          channelConnectionID,
          username: channel?.externalAccountUserName || null,
          photo: channel?.externalAccountPhoto || null,
        });
        setStatus("success");
        // «Быстрый старт» и список каналов должны увидеть подключённый аккаунт
        queryClient.invalidateQueries({ queryKey: ["onboarding"] });
      } catch (err: any) {
        const details =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "";
        setError({ text: humanizeError(details), details });
        setStatus("error");
      }
    };
    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = () =>
    navigate(
      `/admin/connect/instagram${isOnboarding ? "?returnTo=onboarding" : ""}`,
    );
  const goHome = () => navigate("/admin/stats");

  if (status === "loading") {
    return (
      <InstagramConnectLayout
        title="Подключаем Instagram"
        subtitle="Это займёт несколько секунд"
      >
        <div className="flex flex-col items-center text-center py-10">
          <CircularProgress size={48} sx={{ color: "#d62976" }} />
          <div className="text-xl font-semibold mt-6 mb-2">Проверяем вход…</div>
          <div className="text-gray-500">
            Получаем доступ к Директ и комментариям. Не закрывайте страницу.
          </div>
        </div>
      </InstagramConnectLayout>
    );
  }

  if (status === "error") {
    return (
      <InstagramConnectLayout
        title="Не получилось подключить"
        subtitle="Ничего страшного — попробуйте ещё раз"
        backLabel="На главную"
        onBack={goHome}
      >
        <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-6">
          <FaExclamation size={22} />
        </div>
        <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-3">
          Instagram не подключён
        </h2>
        <p className="text-gray-500 mb-6">{error?.text}</p>
        <div className="rounded-xl bg-gray-50 border px-4 py-3 mb-8 text-sm text-gray-600">
          Проверьте, что аккаунт профессиональный (Бизнес или Автор) и что на
          странице Instagram вы нажали «Разрешить».
        </div>
        <button
          type="button"
          onClick={retry}
          className="w-full flex items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-6 py-4 text-lg font-medium text-gray-900 transition-all hover:border-gray-400 hover:shadow-md"
        >
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
            style={{ background: INSTAGRAM_GRADIENT }}
          >
            <FaInstagram size={18} />
          </span>
          Попробовать снова
        </button>
        {error?.details && error.details !== error.text && (
          <details className="mt-4 text-xs text-gray-400">
            <summary className="cursor-pointer">
              Подробности для поддержки
            </summary>
            <div className="mt-2 break-words">{error.details}</div>
          </details>
        )}
      </InstagramConnectLayout>
    );
  }

  const chatbotPath = `/admin/automations/create/keyword?onboarding=1&channelConnectionID=${account!.channelConnectionID}`;
  const accountName = account?.username
    ? `@${account.username}`
    : "Ваш аккаунт";

  return (
    <InstagramConnectLayout
      title="Instagram подключён!"
      subtitle={
        account?.username
          ? `@${account.username} готов к автоответам`
          : "Аккаунт готов к автоответам"
      }
      illustration={
        account ? (
          <AccountIllustration photo={account.photo} />
        ) : (
          <InstagramIllustration />
        )
      }
      backLabel="На главную"
      onBack={goHome}
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 px-3 py-1.5 text-sm font-medium mb-5">
        <FaCheck size={12} />
        Аккаунт успешно подключён
      </div>
      <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-3">
        {accountName} подключён к Ubee AI
      </h2>
      <p className="text-gray-500 mb-6">
        Теперь создадим первого чат-бота: клиент пишет кодовое слово «цена» в
        Директ — бот сразу отвечает. Мы уже всё заполнили, останется проверить
        текст и нажать «Создать».
      </p>

      <ul className="flex flex-col gap-3 mb-8">
        {[
          "Кодовое слово и ответ уже заполнены",
          "Займёт меньше минуты",
          "Текст можно поменять в любой момент",
        ].map((item) => (
          <li key={item} className="flex items-center gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
              <FaCheck size={10} />
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => navigate(chatbotPath)}
        className="w-full flex items-center justify-center gap-3 rounded-xl px-6 py-4 text-lg font-semibold text-white transition-all hover:opacity-95 hover:shadow-lg"
        style={{
          background: INSTAGRAM_GRADIENT,
          boxShadow: "0 8px 24px rgba(214, 41, 118, 0.25)",
        }}
      >
        <FaRobot size={20} />
        Создать чат-бота
      </button>
      <button
        type="button"
        onClick={() =>
          navigate(`/admin/channels/${account!.channelConnectionID}`)
        }
        className="w-full mt-3 rounded-xl px-6 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
      >
        Перейти к настройкам канала
      </button>
    </InstagramConnectLayout>
  );
};

export default InstagramOAuth;
